import React, { useState, useEffect } from 'react';
import { Download, Upload, Database, Trash2, AlertTriangle, RotateCcw, Link2, UserPlus, RefreshCw, CheckCircle, Eraser, DollarSign, Eye, Send, Shield, Lock, Unlock, History } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SystemTools = () => {
  const [loading, setLoading] = useState(false);
  const [exportDestination, setExportDestination] = useState('download');
  const [serverFiles, setServerFiles] = useState([]);
  const [backupSettings, setBackupSettings] = useState({
    enabled: false,
    frequency: 'manual',
    retention_days: 30,
    time: '02:00'
  });
  const [backups, setBackups] = useState([]);
  // ===== Auto-Backup Sistem (Git + DB Dumps) state =====
  const [sysBackup, setSysBackup] = useState({ commits: [], db_dumps: [] });
  const [sysBackupLoading, setSysBackupLoading] = useState(false);
  const [sysBackupTriggering, setSysBackupTriggering] = useState(false);
  const [sysBackupResult, setSysBackupResult] = useState(null);
  const [previewData, setPreviewData] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);

  // ===== RADIUS Orphan Tools state =====
  const [orphanLoading, setOrphanLoading] = useState(false);
  const [orphanLinkable, setOrphanLinkable] = useState(null);
  const [orphanLinking, setOrphanLinking] = useState(false);
  const [linkResult, setLinkResult] = useState(null);
  const [staleSecrets, setStaleSecrets] = useState(null);
  const [staleSelected, setStaleSelected] = useState([]);
  const [deletingSecrets, setDeletingSecrets] = useState(false);
  const [deleteSecretResult, setDeleteSecretResult] = useState(null);

  const loadOrphanPreview = async () => {
    setOrphanLoading(true); setLinkResult(null);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API_URL}/api/v2/admin/orphan-radius-preview`, { headers: { Authorization: `Bearer ${token}` } });
      setOrphanLinkable(res.data || { count: 0, data: [] });
    } catch (e) { toast.error('Gagal memuat preview orphan'); }
    finally { setOrphanLoading(false); }
  };
  const runAutoLink = async () => {
    if (!window.confirm('Buat customer otomatis untuk SEMUA PPP user RADIUS yang tidak ada di DB?')) return;
    setOrphanLinking(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(`${API_URL}/api/v2/admin/auto-link-orphan-radius`, {}, { headers: { Authorization: `Bearer ${token}` } });
      setLinkResult(res.data);
      toast.success(`${res.data?.linked_count || 0} customer berhasil dibuat`);
      setTimeout(loadOrphanPreview, 500);
    } catch (e) { toast.error('Auto-link gagal: ' + (e?.response?.data?.detail || e.message)); }
    finally { setOrphanLinking(false); }
  };
  const loadStaleSecrets = async () => {
    setOrphanLoading(true); setDeleteSecretResult(null);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API_URL}/api/v2/admin/orphan-radius-secrets`, { headers: { Authorization: `Bearer ${token}` } });
      setStaleSecrets(res.data || { count: 0, data: [] }); setStaleSelected([]);
    } catch (e) { toast.error('Gagal memuat stale secrets'); }
    finally { setOrphanLoading(false); }
  };
  const toggleStaleSelect = (u) => setStaleSelected(prev => prev.includes(u) ? prev.filter(x => x !== u) : [...prev, u]);
  const selectAllStale = () => {
    if (!staleSecrets?.data?.length) return;
    const all = staleSecrets.data.map(s => s.username);
    setStaleSelected(staleSelected.length === all.length ? [] : all);
  };
  const deleteSelectedStaleSecrets = async () => {
    if (staleSelected.length === 0) { toast.warning('Pilih minimal 1 username'); return; }
    if (!window.confirm(`Hapus ${staleSelected.length} entry radcheck+radreply? Tindakan permanen.`)) return;
    setDeletingSecrets(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.delete(`${API_URL}/api/v2/admin/orphan-radius-secrets`, { headers: { Authorization: `Bearer ${token}` }, data: { usernames: staleSelected } });
      setDeleteSecretResult(res.data);
      toast.success(`${res.data?.deleted_count || 0} entry dihapus`);
      setTimeout(loadStaleSecrets, 300);
    } catch (e) { toast.error('Hapus gagal: ' + (e?.response?.data?.detail || e.message)); }
    finally { setDeletingSecrets(false); }
  };


  // ===== Bulk Mark Paid state =====
  const [bmpAccounts, setBmpAccounts] = useState(null);
  const [bmpForm, setBmpForm] = useState(() => {
    const now = new Date();
    const m = now.getMonth() + 1;
    const y = now.getFullYear();
    return {
      period_month: m,
      period_year: y,
      cash_account_code: '',
      revenue_account_code: '',
      payment_method: 'cash',
      paid_date: '',
      next_due_date: '',
    };
  });
  const [bmpPreview, setBmpPreview] = useState(null);
  const [bmpLoading, setBmpLoading] = useState(false);
  const [bmpCommitting, setBmpCommitting] = useState(false);

  const loadBmpAccounts = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API_URL}/api/v2/accounting/accounts`, { headers: { Authorization: `Bearer ${token}` } });
      const list = res.data?.data || res.data || [];
      setBmpAccounts(list);
      // Auto-select first Asset & Revenue if empty
      setBmpForm(prev => {
        if (prev.cash_account_code && prev.revenue_account_code) return prev;
        const asset = list.find(a => (a.account_type || '').toLowerCase() === 'asset' && (a.code || '').startsWith('1.11.1'));
        const rev = list.find(a => ['revenue','pendapatan','income'].includes((a.account_type || '').toLowerCase()) && (a.code || '').startsWith('4'));
        return {
          ...prev,
          cash_account_code: prev.cash_account_code || (asset ? asset.code : ''),
          revenue_account_code: prev.revenue_account_code || (rev ? rev.code : ''),
        };
      });
    } catch (e) {
      toast.error('Gagal memuat Chart of Accounts');
    }
  };

  const callBulkPaid = async (dryRun) => {
    if (!bmpForm.cash_account_code || !bmpForm.revenue_account_code) {
      toast.warning('Pilih akun Kas & Pendapatan terlebih dahulu');
      return;
    }
    const payload = {
      period_month: parseInt(bmpForm.period_month),
      period_year: parseInt(bmpForm.period_year),
      cash_account_code: bmpForm.cash_account_code,
      revenue_account_code: bmpForm.revenue_account_code,
      payment_method: bmpForm.payment_method,
      dry_run: dryRun,
    };
    if (bmpForm.paid_date) payload.paid_date = bmpForm.paid_date;
    if (bmpForm.next_due_date) payload.next_due_date = bmpForm.next_due_date;

    if (dryRun) setBmpLoading(true); else setBmpCommitting(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(`${API_URL}/api/v2/admin/bulk-mark-paid`, payload, { headers: { Authorization: `Bearer ${token}` } });
      setBmpPreview(res.data);
      if (dryRun) {
        toast.success(`Preview: ${res.data.eligible_count} customer (Rp ${(res.data.total_amount || 0).toLocaleString('id-ID')})`);
      } else {
        toast.success(`Sukses: ${res.data.processed_count} pelunasan dibuat (Rp ${(res.data.total_amount || 0).toLocaleString('id-ID')})`);
      }
    } catch (e) {
      toast.error('Bulk-mark-paid gagal: ' + (e?.response?.data?.detail || e.message));
    } finally {
      if (dryRun) setBmpLoading(false); else setBmpCommitting(false);
    }
  };

  const commitBulkPaid = async () => {
    if (!bmpPreview || bmpPreview.dry_run !== true) {
      toast.warning('Jalankan Preview terlebih dahulu');
      return;
    }
    const cnt = bmpPreview.eligible_count;
    const amt = bmpPreview.total_amount;
    if (!cnt) { toast.info('Tidak ada customer yang eligible'); return; }
    const msg = `Konfirmasi pelunasan ${cnt} customer untuk periode ${bmpPreview.period}?\n` +
      `Total: Rp ${(amt || 0).toLocaleString('id-ID')}\n` +
      `Dr ${bmpPreview.cash_account?.code} ${bmpPreview.cash_account?.name}\n` +
      `Cr ${bmpPreview.revenue_account?.code} ${bmpPreview.revenue_account?.name}\n` +
      `next_due_date -> ${bmpPreview.next_due_date}\n\n` +
      `Tindakan ini akan membuat ${cnt} invoice + payment + jurnal sekaligus. Lanjutkan?`;
    if (!window.confirm(msg)) return;
    await callBulkPaid(false);
  };


  // ===== Login Audit & Lockout state =====
  const [lockouts, setLockouts] = useState(null);
  const [auditLogs, setAuditLogs] = useState(null);
  const [auditFilterUsername, setAuditFilterUsername] = useState('');
  const [auditOnlyFailed, setAuditOnlyFailed] = useState(false);
  const [loadingLockouts, setLoadingLockouts] = useState(false);
  const [loadingAudit, setLoadingAudit] = useState(false);
  const [unlockingUsername, setUnlockingUsername] = useState(null);

  const loadLockouts = async () => {
    setLoadingLockouts(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API_URL}/api/v2/admin/login-lockouts`, { headers: { Authorization: `Bearer ${token}` } });
      setLockouts(res.data || { count: 0, data: [] });
    } catch (e) { toast.error('Gagal memuat lockout list: ' + (e?.response?.data?.detail || e.message)); }
    finally { setLoadingLockouts(false); }
  };

  const loadAudit = async () => {
    setLoadingAudit(true);
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams({ limit: '100' });
      if (auditFilterUsername.trim()) params.set('username', auditFilterUsername.trim());
      if (auditOnlyFailed) params.set('only_failed', 'true');
      const res = await axios.get(`${API_URL}/api/v2/admin/login-audit?${params}`, { headers: { Authorization: `Bearer ${token}` } });
      setAuditLogs(res.data || { count: 0, data: [] });
    } catch (e) { toast.error('Gagal memuat audit: ' + (e?.response?.data?.detail || e.message)); }
    finally { setLoadingAudit(false); }
  };

  const unlockUser = async (username) => {
    if (!window.confirm(`Unlock akun "${username}"? Semua failed-attempts dalam 10 menit terakhir akan dihapus.`)) return;
    setUnlockingUsername(username);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(`${API_URL}/api/v2/admin/unlock-login`, { username }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(`${username} di-unlock (${res.data?.cleared_attempts || 0} attempts cleared)`);
      setTimeout(loadLockouts, 300);
    } catch (e) { toast.error('Unlock gagal: ' + (e?.response?.data?.detail || e.message)); }
    finally { setUnlockingUsername(null); }
  };

  useEffect(() => {
    fetchServerFiles();
    fetchBackupSettings();
    fetchBackups();
    fetchSysBackupList();
    loadBmpAccounts();
    loadLockouts();
  }, []);


  const fetchServerFiles = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/tools/list-exports`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.success) {
        setServerFiles(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching server files:', error);
    }
  };

  const fetchBackupSettings = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/v2/tools/backup-settings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.success) {
        setBackupSettings(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching backup settings:', error);
    }
  };

  const fetchBackups = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/v2/tools/list-backups`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.success) {
        setBackups(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching backups:', error);
    }
  };

  // ===== Auto-Backup Sistem (Git + DB Dumps) =====
  const fetchSysBackupList = async () => {
    setSysBackupLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/v2/admin/backup/list`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.success) {
        setSysBackup({
          commits: response.data.commits || [],
          db_dumps: response.data.db_dumps || []
        });
      }
    } catch (error) {
      console.error('Error fetching sys backups:', error);
      toast.error('Gagal memuat daftar backup sistem');
    } finally {
      setSysBackupLoading(false);
    }
  };

  const triggerSysBackup = async () => {
    if (!window.confirm('Jalankan backup sistem sekarang? (Git snapshot + Dump DB Radius)')) return;
    setSysBackupTriggering(true);
    setSysBackupResult(null);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API_URL}/api/v2/admin/backup/trigger`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSysBackupResult(response.data);
      if (response.data.success) {
        toast.success('Backup sistem berhasil dijalankan');
        fetchSysBackupList();
      } else {
        toast.error('Backup gagal: ' + (response.data.stderr || 'unknown error'));
      }
    } catch (error) {
      const msg = error?.response?.data?.detail || error.message;
      setSysBackupResult({ success: false, output: '', stderr: msg });
      toast.error('Trigger backup gagal: ' + msg);
    } finally {
      setSysBackupTriggering(false);
    }
  };

  const formatBytes = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
  };

  const handleExport = async (type, format) => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      
      if (exportDestination === 'server') {
        // Save to server
        const response = await axios.get(
          `${API_URL}/api/v2/export-${type}?format=${format}&destination=server`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        
        if (response.data.success) {
          toast.success(`File saved to server: ${response.data.filename}`);
          fetchServerFiles(); // Refresh file list
        }
      } else {
        // Download to local
        const response = await axios.get(
          `${API_URL}/api/v2/export-${type}?format=${format}&destination=download`,
          {
            headers: { Authorization: `Bearer ${token}` },
            responseType: 'blob'
          }
        );
        
        // Determine file extension
        let extension = 'json';
        if (format === 'csv') extension = 'csv';
        if (format === 'excel') extension = 'xlsx';
        
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `${type}_export.${extension}`);
        document.body.appendChild(link);
        link.click();
        link.remove();
        
        toast.success(`Export ${type} downloaded (${format.toUpperCase()})`);
      }
    } catch (error) {
      toast.error(`Gagal export ${type}`);
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadFromServer = async (filename) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API_URL}/api/tools/download-export/${filename}`,
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob'
        }
      );
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success(`Downloaded: ${filename}`);
    } catch (error) {
      toast.error('Gagal download file');
    }
  };

  const handleDeleteFromServer = async (filename) => {
    if (!window.confirm(`Hapus file ${filename}?`)) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_URL}/api/tools/delete-export/${filename}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('File deleted');
      fetchServerFiles();
    } catch (error) {
      toast.error('Gagal delete file');
    }
  };

  const handleImportUsers = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('file', file);
      
      await axios.post(`${API_URL}/api/v2/import-users`, 
        formData,
        { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' } }
      );
      
      toast.success('Import users berhasil');
    } catch (error) {
      toast.error('Gagal import users: ' + (error.response?.data?.detail || error.message));
    } finally {
      setLoading(false);
      event.target.value = '';
    }
  };

  // Universal import handler
  const handleImport = async (type, format, event) => {
    const file = event.target.files[0];
    if (!file) return;

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      
      if (format === 'json') {
        // Handle JSON import
        const text = await file.text();
        const data = JSON.parse(text);
        
        const response = await axios.post(`${API_URL}/api/v2/import-${type}`, 
          { data },
          { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' } }
        );
        toast.success(response.data.message || `Import ${type} berhasil`);
      } else {
        // Handle CSV/Excel import
        const formData = new FormData();
        formData.append('file', file);
        
        const response = await axios.post(`${API_URL}/api/v2/import-${type}`, 
          formData,
          { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' } }
        );
        toast.success(response.data.message || `Import ${type} berhasil`);
      }
    } catch (error) {
      toast.error(`Gagal import ${type}: ` + (error.response?.data?.detail || error.message));
    } finally {
      setLoading(false);
      event.target.value = '';
    }
  };

  const handleResetInvoices = async () => {
    if (!window.confirm('⚠️ PERINGATAN! Ini akan menghapus SEMUA tagihan. Yakin ingin melanjutkan?')) {
      return;
    }
    
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.delete(`${API_URL}/api/v2/tools/reset-invoices`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(response?.data?.message || 'Semua tagihan berhasil dihapus');
      } catch (error) {
      toast.error('Gagal reset tagihan');
    } finally {
      setLoading(false);
    }
  };

  const handleResetPayments = async () => {
    if (!window.confirm('⚠️ PERINGATAN! Ini akan menghapus SEMUA pembayaran. Yakin ingin melanjutkan?')) {
      return;
    }
    
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.delete(`${API_URL}/api/v2/tools/reset-payments`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(response?.data?.message || 'Semua pembayaran berhasil dihapus');
      } catch (error) {
      toast.error('Gagal reset pembayaran');
    } finally {
      setLoading(false);
    }
  };

  const handleResetDatabase = async () => {
    const confirm1 = window.confirm('⚠️⚠️⚠️ BAHAYA! Ini akan menghapus SEMUA data kecuali users. Yakin?');
    if (!confirm1) return;
    
    const confirm2 = window.confirm('⚠️⚠️⚠️ Apakah Anda BENAR-BENAR YAKIN? Data tidak bisa dikembalikan!');
    if (!confirm2) return;
    
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.delete(`${API_URL}/api/v2/tools/reset-database`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(response?.data?.message || 'Database berhasil direset');
      } catch (error) {
      toast.error('Gagal reset database');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateBackupSettings = async (newSettings) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API_URL}/api/v2/tools/backup-settings`, newSettings, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBackupSettings(newSettings);
      toast.success('Backup settings updated');
    } catch (error) {
      toast.error('Gagal update settings');
    }
  };

  // RESTORE_FROM_LOCAL_STORAGE_V1
  const restoreFileInputRef = React.useRef(null);
  const handlePickRestoreFile = () => {
    if (restoreFileInputRef.current) restoreFileInputRef.current.click();
  };
  const handleRestoreFromUpload = async (event) => {
    const file = event.target.files && event.target.files[0];
    if (!file) return;
    // Reset input so the same file can be re-selected
    event.target.value = '';
    const name = file.name || '';
    const lower = name.toLowerCase();
    if (!(lower.endsWith('.json.gz') || lower.endsWith('.sql.gz'))) {
      toast.error('File harus .json.gz (tenant backup) atau .sql.gz (full DB)');
      return;
    }
    const sizeMB = (file.size / (1024 * 1024)).toFixed(2);
    const kind = lower.endsWith('.sql.gz') ? 'FULL DATABASE (semua tenant)' : 'tenant Anda';
    const msg = 'PERINGATAN: Restore akan MENGHAPUS data ' + kind + ' saat ini dan menggantinya dengan isi file berikut. Aksi TIDAK BISA DIBATALKAN.\n\n' + name + ' (' + sizeMB + ' MB)\n\nLanjut?';
    const ok = window.confirm(msg);
    if (!ok) return;

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('file', file);
      const response = await axios.post(
        API_URL + '/api/v2/tools/restore-backup-upload',
        formData,
        {
          headers: {
            Authorization: 'Bearer ' + token,
            'Content-Type': 'multipart/form-data',
          },
          timeout: 300000,  // 5 min
        }
      );
      const msg = response?.data?.message || 'Restore berhasil';
      toast.success(msg, { duration: 8000 });
      fetchBackups();
    } catch (error) {
      const detail = error?.response?.data?.detail || error?.message || 'Gagal restore file upload';
      toast.error('Gagal restore: ' + detail, { duration: 10000 });
    } finally {
      setLoading(false);
    }
  };

  const handleCreateBackup = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API_URL}/api/v2/tools/create-backup`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Backup created: ${response.data.filename}`);
      fetchBackups();
    } catch (error) {
      toast.error('Gagal create backup');
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadBackup = async (filename) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API_URL}/api/v2/tools/download-backup/${filename}`,
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob'
        }
      );
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success('Backup downloaded');
    } catch (error) {
      toast.error('Gagal download backup');
    }
  };

  const handleDeleteBackup = async (filename) => {
    if (!window.confirm(`Hapus backup ${filename}?`)) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_URL}/api/v2/tools/delete-backup/${filename}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Backup deleted');
      fetchBackups();
    } catch (error) {
      toast.error('Gagal delete backup');
    }
  };

  const handleRestoreBackup = async (filename) => {
    setPreviewLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API_URL}/api/v2/tools/preview-backup/${filename}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPreviewData(res?.data?.data || null);
    } catch (error) {
      const detail = error?.response?.data?.detail || error?.message || 'Gagal preview backup';
      toast.error('Preview gagal: ' + detail);
    } finally {
      setPreviewLoading(false);
    }
  };

  const confirmRestoreFromPreview = async () => {
    if (!previewData) return;
    const filename = previewData.filename;
    const isFullDb = previewData.type === 'full';
    const targetLabel = isFullDb ? 'SELURUH DATABASE (semua tenant)' : 'data tenant Anda';
    const ok = window.confirm(
      `⚠️ KONFIRMASI TERAKHIR

Restore ${targetLabel} dari ${filename}.
Semua data ${targetLabel.toLowerCase()} saat ini AKAN DIHAPUS dan diganti dengan isi backup.

Klik OK untuk lanjut, Cancel untuk batal.`
    );
    if (!ok) return;

    setLoading(true);
    setPreviewData(null);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API_URL}/api/v2/tools/restore-backup/${filename}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const msg = response?.data?.message || 'Restore berhasil';
      toast.success(msg, { duration: 6000 });
        fetchBackups();
    } catch (error) {
      const detail = error?.response?.data?.detail || error?.message || 'Gagal restore backup';
      toast.error('Gagal restore: ' + detail, { duration: 8000 });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950 space-y-4 animate-fade-in p-6">
      {previewData && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={() => setPreviewData(null)} data-testid="restore-preview-overlay">
          <div className="bg-white dark:bg-slate-800 rounded-xl max-w-2xl w-full max-h-[85vh] overflow-y-auto shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="px-6 py-4 border-b border-slate-200 dark:border-slate-700 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-slate-800 dark:text-white flex items-center gap-2">
                <RotateCcw className="w-5 h-5 text-emerald-600 dark:text-emerald-400" /> Preview Restore Backup
              </h3>
              <button onClick={() => setPreviewData(null)} className="text-slate-500 hover:text-slate-700 dark:hover:text-slate-200 text-2xl leading-none dark:text-slate-400" data-testid="btn-preview-close">×</button>
            </div>
            <div className="p-6 space-y-4">
              <div className="bg-slate-50 dark:bg-slate-900 rounded-lg p-4 text-sm space-y-1">
                <div><span className="text-slate-500 dark:text-slate-400">File:</span> <span className="font-mono">{previewData.filename}</span></div>
                <div><span className="text-slate-500 dark:text-slate-400">Tipe:</span> <span className="font-semibold uppercase">{previewData.type === 'full' ? 'FULL DATABASE' : 'Per-Tenant'}</span></div>
                {previewData.meta?.tenant_name && (
                  <div><span className="text-slate-500 dark:text-slate-400">Tenant:</span> <span className="font-semibold">{previewData.meta.tenant_name}</span></div>
                )}
                {previewData.meta?.backup_date && (
                  <div><span className="text-slate-500 dark:text-slate-400">Tanggal Backup:</span> {new Date(previewData.meta.backup_date).toLocaleString('id-ID')}</div>
                )}
                <div><span className="text-slate-500 dark:text-slate-400">Ukuran File:</span> {formatBytes(previewData.size || 0)}</div>
                {previewData.meta?.row_count != null && (
                  <div><span className="text-slate-500 dark:text-slate-400">Total Baris Data:</span> <span className="font-semibold">{Number(previewData.meta.row_count).toLocaleString('id-ID')}</span></div>
                )}
                {previewData.meta?.tables_count != null && (
                  <div><span className="text-slate-500 dark:text-slate-400">Jumlah Tabel:</span> {previewData.meta.tables_count}</div>
                )}
                {previewData.meta?.created_by && (
                  <div><span className="text-slate-500 dark:text-slate-400">Dibuat oleh:</span> {previewData.meta.created_by}</div>
                )}
              </div>

              {previewData.summary && previewData.summary.length > 0 && (
                <div>
                  <h4 className="text-sm font-semibold mb-2 text-slate-700 dark:text-slate-200">Detail Isi Backup</h4>
                  <div className="border border-slate-200 dark:border-slate-700 rounded-lg overflow-hidden">
                    <table className="w-full text-sm">
                      <thead className="bg-slate-100 dark:bg-slate-900">
                        <tr>
                          <th className="text-left px-3 py-2 font-medium text-slate-600 dark:text-slate-300">Tabel</th>
                          <th className="text-right px-3 py-2 font-medium text-slate-600 dark:text-slate-300">Jumlah Baris</th>
                        </tr>
                      </thead>
                      <tbody>
                        {previewData.summary.map((s, i) => (
                          <tr key={s.table} className={i % 2 === 0 ? 'bg-white dark:bg-slate-800' : 'bg-slate-50 dark:bg-slate-900/50'}>
                            <td className="px-3 py-1.5 font-mono text-xs">{s.table}</td>
                            <td className="px-3 py-1.5 text-right font-semibold">{Number(s.rows).toLocaleString('id-ID')}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3 text-sm text-red-700 dark:text-red-400">
                <AlertTriangle className="w-4 h-4 inline mr-1" />
                <strong>PERINGATAN:</strong> Restore akan menghapus data {previewData.type === 'full' ? 'SELURUH database (semua tenant)' : 'tenant Anda'} saat ini dan menggantinya dengan isi backup ini. Tindakan tidak dapat dibatalkan.
              </div>

              <div className="flex flex-col sm:flex-row gap-2 justify-end pt-2">
                <Button variant="outline" onClick={() => setPreviewData(null)} data-testid="btn-preview-cancel">Batal</Button>
                <Button className="bg-emerald-600 hover:bg-emerald-700 text-white" onClick={confirmRestoreFromPreview} disabled={loading} data-testid="btn-preview-confirm-restore">
                  <RotateCcw className="w-4 h-4 mr-1" /> Lanjut Restore
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {previewLoading && (
        <div className="fixed inset-0 z-50 bg-black/30 flex items-center justify-center" data-testid="preview-loading">
          <div className="bg-white dark:bg-slate-800 rounded-lg px-6 py-4 shadow-xl text-sm text-slate-700 dark:text-slate-200">
            Memuat preview backup…
          </div>
        </div>
      )}
      <h1 className="text-2xl font-bold text-slate-800 dark:text-white mb-6">Tools Sistem</h1>

      {/* ============ Auto-Backup Sistem (Git Snapshot + DB Dump) ============ */}
      <Card className="border-0 shadow-lg" data-testid="card-sys-backup">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <Database className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
            Auto-Backup Sistem (Git Snapshot + Dump DB Radius)
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 p-4 bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800 rounded-lg">
            <div className="text-sm text-slate-700 dark:text-slate-200">
              <div className="font-semibold flex items-center gap-2 mb-1">
                <History className="w-4 h-4" />
                Jadwal Otomatis: Harian (cron 02:00 WIB)
              </div>
              <div className="text-xs text-slate-600 dark:text-slate-400">
                Snapshot codebase (git bare repo) + dump database <span className="font-mono">radius</span> tergzip. Disimpan di server.
              </div>
            </div>
            <Button
              onClick={triggerSysBackup}
              disabled={sysBackupTriggering}
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
              data-testid="btn-sys-backup-trigger"
            >
              {sysBackupTriggering ? (
                <><RefreshCw className="w-4 h-4 mr-2 animate-spin" /> Sedang Backup...</>
              ) : (
                <><Database className="w-4 h-4 mr-2" /> Jalankan Backup Sekarang</>
              )}
            </Button>
          </div>

          {sysBackupResult && (
            <div className={`text-xs font-mono p-3 rounded border ${sysBackupResult.success ? 'bg-green-50 border-green-200 text-green-800 dark:bg-green-900/20 dark:text-green-300 dark:border-green-800' : 'bg-red-50 border-red-200 text-red-800 dark:bg-red-900/20 dark:text-red-300 dark:border-red-800'}`} data-testid="sys-backup-result">
              <div className="font-semibold mb-1">{sysBackupResult.success ? 'Output backup:' : 'Error backup:'}</div>
              <div className="whitespace-pre-wrap break-all">{sysBackupResult.output || sysBackupResult.stderr || '(no output)'}</div>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Git commits */}
            <div className="border border-slate-200 dark:border-slate-700 rounded-lg overflow-hidden">
              <div className="px-4 py-2 bg-slate-100 dark:bg-slate-900 flex items-center justify-between">
                <h4 className="text-sm font-semibold text-slate-700 dark:text-slate-200 flex items-center gap-2">
                  <History className="w-4 h-4" /> Git Snapshots (20 terakhir)
                </h4>
                <button
                  onClick={fetchSysBackupList}
                  disabled={sysBackupLoading}
                  className="text-xs text-slate-500 hover:text-slate-700 dark:hover:text-slate-200 dark:text-slate-400"
                  data-testid="btn-sys-backup-refresh"
                >
                  <RefreshCw className={`w-3 h-3 inline ${sysBackupLoading ? 'animate-spin' : ''}`} />
                </button>
              </div>
              <div className="max-h-64 overflow-y-auto divide-y divide-slate-100 dark:divide-slate-800" data-testid="list-sys-backup-commits">
                {sysBackup.commits.length === 0 ? (
                  <div className="text-center text-xs text-slate-400 py-6 dark:text-slate-500">Tidak ada commit</div>
                ) : sysBackup.commits.map((c) => (
                  <div key={c.hash} className="px-3 py-2 text-xs flex items-start gap-2">
                    <span className="font-mono text-amber-700 dark:text-amber-400 shrink-0">{c.hash}</span>
                    <span className="text-slate-700 dark:text-slate-300 break-all">{c.msg}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* DB Dumps */}
            <div className="border border-slate-200 dark:border-slate-700 rounded-lg overflow-hidden">
              <div className="px-4 py-2 bg-slate-100 dark:bg-slate-900">
                <h4 className="text-sm font-semibold text-slate-700 dark:text-slate-200 flex items-center gap-2">
                  <Database className="w-4 h-4" /> DB Dumps (terbaru)
                </h4>
              </div>
              <div className="max-h-64 overflow-y-auto divide-y divide-slate-100 dark:divide-slate-800" data-testid="list-sys-backup-dumps">
                {sysBackup.db_dumps.length === 0 ? (
                  <div className="text-center text-xs text-slate-400 py-6 dark:text-slate-500">Belum ada dump</div>
                ) : sysBackup.db_dumps.map((d) => (
                  <div key={d.file} className="px-3 py-2 text-xs flex items-center justify-between gap-2">
                    <span className="font-mono text-slate-700 dark:text-slate-300 break-all">{d.file}</span>
                    <span className="text-slate-500 dark:text-slate-400 shrink-0">{d.size_mb} MB</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>


      {/* Export Destination Selector */}
      <Card className="border-0 shadow-lg">
        <CardHeader>
          <CardTitle className="text-base">Pilih Lokasi Penyimpanan Export</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex gap-4">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="radio"
                name="destination"
                value="download"
                checked={exportDestination === 'download'}
                onChange={(e) => setExportDestination(e.target.value)}
                className="w-4 h-4"
              />
              <span className="text-sm">Download ke Local Drive</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="radio"
                name="destination"
                value="server"
                checked={exportDestination === 'server'}
                onChange={(e) => setExportDestination(e.target.value)}
                className="w-4 h-4"
              />
              <span className="text-sm">Simpan di Server</span>
            </label>
          </div>
          <p className="text-xs text-slate-500 dark:text-slate-400 mt-2">
            {exportDestination === 'download' 
              ? '📥 File akan langsung ter-download ke komputer Anda' 
              : '💾 File akan disimpan di server dan bisa didownload kapan saja'}
          </p>
        </CardContent>
      </Card>

      {/* Export/Import */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Export Section */}
        <Card className="border-0 shadow-lg">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Download className="w-5 h-5" />
              Export Data
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Export Users */}
            <div>
              <p className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">Export Users</p>
              <div className="flex gap-2">
                <Button 
                  onClick={() => handleExport('users', 'json')}
                  disabled={loading}
                  className="flex-1 text-xs"
                  variant="outline"
                  size="sm"
                >
                  JSON
                </Button>
                <Button 
                  onClick={() => handleExport('users', 'csv')}
                  disabled={loading}
                  className="flex-1 text-xs"
                  variant="outline"
                  size="sm"
                >
                  CSV
                </Button>
                <Button 
                  onClick={() => handleExport('users', 'excel')}
                  disabled={loading}
                  className="flex-1 text-xs"
                  variant="outline"
                  size="sm"
                >
                  Excel
                </Button>
              </div>
            </div>

            {/* Export Customers */}
            <div>
              <p className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">Export Customers</p>
              <div className="flex gap-2">
                <Button 
                  onClick={() => handleExport('customers', 'json')}
                  disabled={loading}
                  className="flex-1 text-xs"
                  variant="outline"
                  size="sm"
                >
                  JSON
                </Button>
                <Button 
                  onClick={() => handleExport('customers', 'csv')}
                  disabled={loading}
                  className="flex-1 text-xs"
                  variant="outline"
                  size="sm"
                >
                  CSV
                </Button>
                <Button 
                  onClick={() => handleExport('customers', 'excel')}
                  disabled={loading}
                  className="flex-1 text-xs"
                  variant="outline"
                  size="sm"
                >
                  Excel
                </Button>
              </div>
            </div>

            {/* Export Invoices */}
            <div>
              <p className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">Export Tagihan</p>
              <div className="flex gap-2">
                <Button 
                  onClick={() => handleExport('invoices', 'json')}
                  disabled={loading}
                  className="flex-1 text-xs"
                  variant="outline"
                  size="sm"
                >
                  JSON
                </Button>
                <Button 
                  onClick={() => handleExport('invoices', 'csv')}
                  disabled={loading}
                  className="flex-1 text-xs"
                  variant="outline"
                  size="sm"
                >
                  CSV
                </Button>
                <Button 
                  onClick={() => handleExport('invoices', 'excel')}
                  disabled={loading}
                  className="flex-1 text-xs"
                  variant="outline"
                  size="sm"
                >
                  Excel
                </Button>
              </div>
            </div>

            {/* Export Payments */}
            <div>
              <p className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">Export Pembayaran</p>
              <div className="flex gap-2">
                <Button 
                  onClick={() => handleExport('payments', 'json')}
                  disabled={loading}
                  className="flex-1 text-xs"
                  variant="outline"
                  size="sm"
                >
                  JSON
                </Button>
                <Button 
                  onClick={() => handleExport('payments', 'csv')}
                  disabled={loading}
                  className="flex-1 text-xs"
                  variant="outline"
                  size="sm"
                >
                  CSV
                </Button>
                <Button 
                  onClick={() => handleExport('payments', 'excel')}
                  disabled={loading}
                  className="flex-1 text-xs"
                  variant="outline"
                  size="sm"
                >
                  Excel
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Import Section */}
        <Card className="border-0 shadow-lg">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Upload className="w-5 h-5" />
              Import Data
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Import Users */}
            <div>
              <p className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">Import Users</p>
              <div className="flex gap-2">
                <input type="file" accept=".json" onChange={(e) => handleImport('users', 'json', e)} className="hidden" id="import-users-json" />
                <Button asChild disabled={loading} variant="outline" size="sm" className="cursor-pointer">
                  <label htmlFor="import-users-json">JSON</label>
                </Button>
                <input type="file" accept=".csv" onChange={(e) => handleImport('users', 'csv', e)} className="hidden" id="import-users-csv" />
                <Button asChild disabled={loading} variant="outline" size="sm" className="cursor-pointer">
                  <label htmlFor="import-users-csv">CSV</label>
                </Button>
                <input type="file" accept=".xlsx,.xls" onChange={(e) => handleImport('users', 'excel', e)} className="hidden" id="import-users-excel" />
                <Button asChild disabled={loading} variant="outline" size="sm" className="cursor-pointer">
                  <label htmlFor="import-users-excel">Excel</label>
                </Button>
              </div>
            </div>
            
            {/* Import Customers */}
            <div>
              <p className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">Import Customers</p>
              <div className="flex gap-2">
                <input type="file" accept=".json" onChange={(e) => handleImport('customers', 'json', e)} className="hidden" id="import-customers-json" />
                <Button asChild disabled={loading} variant="outline" size="sm" className="cursor-pointer">
                  <label htmlFor="import-customers-json">JSON</label>
                </Button>
                <input type="file" accept=".csv" onChange={(e) => handleImport('customers', 'csv', e)} className="hidden" id="import-customers-csv" />
                <Button asChild disabled={loading} variant="outline" size="sm" className="cursor-pointer">
                  <label htmlFor="import-customers-csv">CSV</label>
                </Button>
                <input type="file" accept=".xlsx,.xls" onChange={(e) => handleImport('customers', 'excel', e)} className="hidden" id="import-customers-excel" />
                <Button asChild disabled={loading} variant="outline" size="sm" className="cursor-pointer">
                  <label htmlFor="import-customers-excel">Excel</label>
                </Button>
              </div>
            </div>
            
            {/* Import Tagihan */}
            <div>
              <p className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">Import Tagihan</p>
              <div className="flex gap-2">
                <input type="file" accept=".json" onChange={(e) => handleImport('invoices', 'json', e)} className="hidden" id="import-invoices-json" />
                <Button asChild disabled={loading} variant="outline" size="sm" className="cursor-pointer">
                  <label htmlFor="import-invoices-json">JSON</label>
                </Button>
                <input type="file" accept=".csv" onChange={(e) => handleImport('invoices', 'csv', e)} className="hidden" id="import-invoices-csv" />
                <Button asChild disabled={loading} variant="outline" size="sm" className="cursor-pointer">
                  <label htmlFor="import-invoices-csv">CSV</label>
                </Button>
                <input type="file" accept=".xlsx,.xls" onChange={(e) => handleImport('invoices', 'excel', e)} className="hidden" id="import-invoices-excel" />
                <Button asChild disabled={loading} variant="outline" size="sm" className="cursor-pointer">
                  <label htmlFor="import-invoices-excel">Excel</label>
                </Button>
              </div>
            </div>
            
            {/* Import Pembayaran */}
            <div>
              <p className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">Import Pembayaran</p>
              <div className="flex gap-2">
                <input type="file" accept=".json" onChange={(e) => handleImport('payments', 'json', e)} className="hidden" id="import-payments-json" />
                <Button asChild disabled={loading} variant="outline" size="sm" className="cursor-pointer">
                  <label htmlFor="import-payments-json">JSON</label>
                </Button>
                <input type="file" accept=".csv" onChange={(e) => handleImport('payments', 'csv', e)} className="hidden" id="import-payments-csv" />
                <Button asChild disabled={loading} variant="outline" size="sm" className="cursor-pointer">
                  <label htmlFor="import-payments-csv">CSV</label>
                </Button>
                <input type="file" accept=".xlsx,.xls" onChange={(e) => handleImport('payments', 'excel', e)} className="hidden" id="import-payments-excel" />
                <Button asChild disabled={loading} variant="outline" size="sm" className="cursor-pointer">
                  <label htmlFor="import-payments-excel">Excel</label>
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Server Files */}
      {exportDestination === 'server' && serverFiles.length > 0 && (
        <Card className="border-0 shadow-lg">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Database className="w-5 h-5" />
              File Export di Server ({serverFiles.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {serverFiles.map((file) => (
                <div
                  key={file.name}
                  className="flex items-center justify-between p-3 bg-slate-50 dark:bg-slate-800 rounded-lg"
                >
                  <div className="flex-1">
                    <p className="text-sm font-medium text-slate-700 dark:text-slate-300">
                      {file.name}
                    </p>
                    <p className="text-xs text-slate-500 dark:text-slate-400">
                      {formatBytes(file.size)} • {new Date(file.created * 1000).toLocaleString('id-ID')}
                    </p>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleDownloadFromServer(file.name)}
                    >
                      <Download className="w-3 h-3 mr-1" />
                      Download
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      className="text-red-600 dark:text-red-400"
                      onClick={() => handleDeleteFromServer(file.name)}
                    >
                      <Trash2 className="w-3 h-3" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Database Backup */}
      <Card className="border-0 shadow-lg">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Database className="w-5 h-5" />
            Backup & Restore Database
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Settings */}
          <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium">Auto Backup</span>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={backupSettings.enabled}
                  onChange={(e) => handleUpdateBackupSettings({
                    ...backupSettings,
                    enabled: e.target.checked
                  })}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 dark:peer-focus:ring-blue-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-blue-600"></div>
              </label>
            </div>
            
            <div>
              <label className="text-sm text-slate-600 dark:text-slate-400">Frequency</label>
              <select
                value={backupSettings.frequency}
                onChange={(e) => handleUpdateBackupSettings({
                  ...backupSettings,
                  frequency: e.target.value
                })}
                className="w-full mt-1 p-2 border rounded-lg dark:bg-slate-800 dark:text-white"
                disabled={!backupSettings.enabled}
              >
                <option value="manual">Manual Only</option>
                <option value="daily">Setiap Hari</option>
                <option value="weekly">Setiap Minggu</option>
                <option value="monthly">Setiap Bulan</option>
              </select>
            </div>

            <div>
              <label className="text-sm text-slate-600 dark:text-slate-400">Retention Period</label>
              <select
                value={backupSettings.retention_days}
                onChange={(e) => handleUpdateBackupSettings({
                  ...backupSettings,
                  retention_days: parseInt(e.target.value)
                })}
                className="w-full mt-1 p-2 border rounded-lg dark:bg-slate-800 dark:text-white"
              >
                <option value="30">1 Bulan (30 hari)</option>
                <option value="90">3 Bulan (90 hari)</option>
                <option value="180">6 Bulan (180 hari)</option>
                <option value="365">1 Tahun (365 hari)</option>
              </select>
            </div>

            <div>
              <label className="text-sm text-slate-600 dark:text-slate-400">Backup Time</label>
              <input
                type="time"
                value={backupSettings.time}
                onChange={(e) => handleUpdateBackupSettings({
                  ...backupSettings,
                  time: e.target.value
                })}
                className="w-full mt-1 p-2 border rounded-lg dark:bg-slate-800 dark:text-white"
                disabled={!backupSettings.enabled}
              />
              <p className="text-xs text-slate-500 mt-1 dark:text-slate-400">
                {backupSettings.enabled 
                  ? `Auto backup akan berjalan ${backupSettings.frequency === 'daily' ? 'setiap hari' : backupSettings.frequency === 'weekly' ? 'setiap minggu' : backupSettings.frequency === 'monthly' ? 'setiap bulan' : ''} jam ${backupSettings.time}`
                  : 'Auto backup dinonaktifkan'}
              </p>
            </div>
          </div>

          {/* Manual Backup Button + Restore from Local */}
          <div className="flex flex-col sm:flex-row gap-2">
            <Button
              onClick={handleCreateBackup}
              disabled={loading}
              className="flex-1 bg-green-600 hover:bg-green-700"
              data-testid="btn-create-backup-now"
            >
              <Database className="w-4 h-4 mr-2" />
              Create Backup Now
            </Button>
            <input
              ref={restoreFileInputRef}
              type="file"
              accept=".gz,.json.gz,.sql.gz"
              onChange={handleRestoreFromUpload}
              style={{ display: 'none' }}
              data-testid="input-restore-file"
            />
            <Button
              onClick={handlePickRestoreFile}
              disabled={loading}
              variant="outline"
              className="flex-1 border-emerald-500 text-emerald-700 dark:text-emerald-400 bg-emerald-50 hover:bg-emerald-100 dark:bg-emerald-950/30 font-semibold"
              data-testid="btn-restore-from-local"
              title="Upload file backup dari komputer Anda"
            >
              <Upload className="w-4 h-4 mr-2" />
              Restore dari File Lokal
            </Button>
          </div>

          {/* Backup List */}
          {backups.length > 0 && (
            <div className="space-y-2">
              <h4 className="text-sm font-semibold">Backup History ({backups.length})</h4>
              {backups.map((backup) => (
                <div
                  key={backup.name}
                  className="flex flex-col sm:flex-row sm:items-center sm:justify-between p-3 bg-slate-50 dark:bg-slate-800 rounded-lg gap-2"
                >
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-slate-700 dark:text-slate-300 truncate">{backup.name}</p>
                    <p className="text-xs text-slate-500 dark:text-slate-400">
                      {formatBytes(backup.size)} • {new Date(backup.created * 1000).toLocaleString('id-ID')}
                    </p>
                  </div>
                  <div className="flex gap-2 flex-shrink-0">
                    {/* BACKUP_ACTION_LABELS_V1 — add text labels so users know what each icon does */}
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleDownloadBackup(backup.name)}
                      title="Download backup"
                      data-testid={"btn-download-backup-" + backup.name}
                    >
                      <Download className="w-3 h-3 sm:mr-1" />
                      <span className="hidden sm:inline text-xs">Download</span>
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      className="text-emerald-600 border-emerald-300 dark:text-emerald-400 bg-emerald-50 hover:bg-emerald-100 dark:bg-emerald-950/30"
                      onClick={() => handleRestoreBackup(backup.name)}
                      title="Restore dari backup ini"
                      data-testid={"btn-restore-backup-" + backup.name}
                    >
                      <RotateCcw className="w-3 h-3 sm:mr-1" />
                      <span className="hidden sm:inline text-xs font-semibold">Restore</span>
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      className="text-red-600 hidden sm:flex dark:text-red-400"
                      onClick={() => handleDeleteBackup(backup.name)}
                      title="Hapus backup"
                      data-testid={"btn-delete-backup-" + backup.name}
                    >
                      <Trash2 className="w-3 h-3 sm:mr-1" />
                      <span className="hidden sm:inline text-xs">Hapus</span>
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}

          {backups.length === 0 && (
            <div className="text-center py-4 text-slate-500 dark:text-slate-400 text-sm">
              Belum ada backup. Klik "Create Backup Now" untuk membuat backup pertama.
            </div>
          )}
        </CardContent>
      </Card>

      {/* ===== RADIUS Orphan Tools ===== */}
      <Card className="border-0 shadow-lg" data-testid="radius-orphan-tools-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-indigo-700 dark:text-indigo-300">
            <Link2 className="w-5 h-5" />
            RADIUS Orphan Tools
          </CardTitle>
          <p className="text-sm text-slate-500 mt-1 dark:text-slate-400">
            Auto-link PPP user RADIUS yang belum punya row di tabel <code>customers</code>, dan bersihkan secret yang tidak terpakai.
          </p>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* A. Auto-link */}
          <div className="border rounded-lg p-4 bg-indigo-50/40 dark:bg-indigo-900/10">
            <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
              <div>
                <h3 className="font-semibold text-slate-800 dark:text-slate-200 flex items-center gap-2">
                  <UserPlus className="w-4 h-4 text-indigo-600 dark:text-indigo-400" />
                  Auto-Link Orphan RADIUS Users
                </h3>
                <p className="text-xs text-slate-500 mt-0.5 dark:text-slate-400">Membuat customer baru otomatis untuk PPP secret yang punya session aktif/historikal tetapi belum ada di DB.</p>
              </div>
              <div className="flex gap-2">
                <button type="button" onClick={loadOrphanPreview} disabled={orphanLoading} data-testid="btn-preview-orphan"
                  className="px-3 py-2 rounded-md bg-white dark:bg-slate-800 border border-indigo-300 text-indigo-700 text-sm font-medium hover:bg-indigo-50 inline-flex items-center gap-1.5 disabled:opacity-50 dark:text-indigo-300 dark:hover:bg-indigo-900/30">
                  <RefreshCw className={`w-4 h-4 ${orphanLoading ? 'animate-spin' : ''}`} /> Preview
                </button>
                <button type="button" onClick={runAutoLink} disabled={orphanLinking || !orphanLinkable?.count} data-testid="btn-run-autolink"
                  className="px-3 py-2 rounded-md bg-indigo-600 text-white text-sm font-semibold hover:bg-indigo-700 inline-flex items-center gap-1.5 disabled:opacity-50">
                  {orphanLinking ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Link2 className="w-4 h-4" />}
                  Auto-Link {orphanLinkable?.count ? `(${orphanLinkable.count})` : ''}
                </button>
              </div>
            </div>
            {orphanLinkable && (
              <div className="bg-white dark:bg-slate-900 rounded border border-slate-200 dark:border-slate-700 overflow-hidden">
                <div className="px-3 py-2 bg-slate-50 dark:bg-slate-800 text-xs font-semibold text-slate-600 dark:text-slate-300 border-b">{orphanLinkable.count} kandidat ditemukan</div>
                {orphanLinkable.count === 0 ? (
                  <div className="p-4 text-sm text-slate-500 text-center dark:text-slate-400">
                    <CheckCircle className="w-5 h-5 text-green-500 mx-auto mb-1" />
                    Tidak ada orphan — semua PPP user RADIUS sudah link ke customer.
                  </div>
                ) : (
                  <div className="max-h-72 overflow-y-auto [&::-webkit-scrollbar]:hidden [scrollbar-width:none]">
                    <table className="w-full text-xs">
                      <thead className="bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300">
                        <tr>
                          <th className="px-3 py-2 text-left">Username</th>
                          <th className="px-3 py-2 text-left">Router</th>
                          <th className="px-3 py-2 text-left">IP Terakhir</th>
                          <th className="px-3 py-2 text-left">Paket</th>
                          <th className="px-3 py-2 text-center">Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {orphanLinkable.data.map((o, idx) => (
                          <tr key={idx} className="border-t hover:bg-indigo-50/30 dark:hover:bg-indigo-900/20">
                            <td className="px-3 py-2 font-mono">{o.username}</td>
                            <td className="px-3 py-2">{o.router_name}</td>
                            <td className="px-3 py-2 font-mono text-slate-600 dark:text-slate-300">{o.current_ip || '-'}</td>
                            <td className="px-3 py-2">{o.mikrotik_group || <span className="text-amber-600 dark:text-amber-400">-</span>}</td>
                            <td className="px-3 py-2 text-center">
                              {o.has_active_session
                                ? <Badge className="bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300">Online</Badge>
                                : <Badge variant="outline" className="text-slate-500 dark:text-slate-400">Pernah connect</Badge>}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}
            {linkResult && (
              <div className="mt-3 text-sm">
                <div className="text-green-700 dark:text-green-300">✅ Berhasil membuat <b>{linkResult.linked_count}</b> customer.</div>
                {linkResult.skipped_count > 0 && (<div className="text-amber-700 mt-1 dark:text-amber-300">⚠️ Skip {linkResult.skipped_count} (lihat detail).</div>)}
              </div>
            )}
          </div>

          {/* B. Stale Secrets */}
          <div className="border rounded-lg p-4 bg-red-50/40 dark:bg-red-900/10">
            <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
              <div>
                <h3 className="font-semibold text-slate-800 dark:text-slate-200 flex items-center gap-2">
                  <Eraser className="w-4 h-4 text-red-600 dark:text-red-400" />
                  Bersihkan RADIUS Secret Tidak Terpakai
                </h3>
                <p className="text-xs text-slate-500 mt-0.5 dark:text-slate-400">PPP secret yang tidak ada di customer dan terakhir connect &gt; 7 hari lalu.</p>
              </div>
              <div className="flex gap-2">
                <button type="button" onClick={loadStaleSecrets} disabled={orphanLoading} data-testid="btn-preview-stale"
                  className="px-3 py-2 rounded-md bg-white dark:bg-slate-800 border border-red-300 text-red-700 text-sm font-medium hover:bg-red-50 inline-flex items-center gap-1.5 disabled:opacity-50 dark:text-red-300 dark:hover:bg-red-900/30">
                  <RefreshCw className={`w-4 h-4 ${orphanLoading ? 'animate-spin' : ''}`} /> Preview
                </button>
                <button type="button" onClick={deleteSelectedStaleSecrets} disabled={deletingSecrets || staleSelected.length === 0} data-testid="btn-delete-stale"
                  className="px-3 py-2 rounded-md bg-red-600 text-white text-sm font-semibold hover:bg-red-700 inline-flex items-center gap-1.5 disabled:opacity-50">
                  {deletingSecrets ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                  Hapus Terpilih ({staleSelected.length})
                </button>
              </div>
            </div>
            {staleSecrets && (
              <div className="bg-white dark:bg-slate-900 rounded border border-slate-200 dark:border-slate-700 overflow-hidden">
                <div className="px-3 py-2 bg-slate-50 dark:bg-slate-800 text-xs font-semibold text-slate-600 dark:text-slate-300 border-b flex justify-between items-center">
                  <span>{staleSecrets.count} kandidat</span>
                  {staleSecrets.count > 0 && (
                    <button type="button" onClick={selectAllStale} className="text-indigo-600 hover:underline text-xs dark:text-indigo-400">
                      {staleSelected.length === staleSecrets.count ? 'Batal Semua' : 'Pilih Semua'}
                    </button>
                  )}
                </div>
                {staleSecrets.count === 0 ? (
                  <div className="p-4 text-sm text-slate-500 text-center dark:text-slate-400"><CheckCircle className="w-5 h-5 text-green-500 mx-auto mb-1" />Tidak ada secret stale.</div>
                ) : (
                  <div className="max-h-72 overflow-y-auto [&::-webkit-scrollbar]:hidden [scrollbar-width:none]">
                    <table className="w-full text-xs">
                      <thead className="bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300">
                        <tr>
                          <th className="px-3 py-2 text-center w-10"></th>
                          <th className="px-3 py-2 text-left">Username</th>
                          <th className="px-3 py-2 text-left">Terakhir Aktif</th>
                        </tr>
                      </thead>
                      <tbody>
                        {staleSecrets.data.map((o, idx) => (
                          <tr key={idx} className="border-t hover:bg-red-50/30 dark:hover:bg-red-900/20">
                            <td className="px-3 py-2 text-center">
                              <input type="checkbox" checked={staleSelected.includes(o.username)} onChange={() => toggleStaleSelect(o.username)} />
                            </td>
                            <td className="px-3 py-2 font-mono">{o.username}</td>
                            <td className="px-3 py-2 text-slate-600 dark:text-slate-300">{o.last_seen ? new Date(o.last_seen).toLocaleString('id-ID') : '—'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}
            {deleteSecretResult && (
              <div className="mt-3 text-sm">
                <div className="text-green-700 dark:text-green-300">🧹 <b>{deleteSecretResult.deleted_count}</b> secret dihapus.</div>
                {deleteSecretResult.skipped_count > 0 && (<div className="text-amber-700 mt-1 dark:text-amber-300">⚠️ Skip {deleteSecretResult.skipped_count} (tidak aman).</div>)}
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* ===== Bulk Mark Paid ===== */}
      <Card className="border-0 shadow-lg" data-testid="bulk-mark-paid-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-emerald-700 dark:text-emerald-300">
            <DollarSign className="w-5 h-5" />
            Bulk Pelunasan Tagihan Periode
          </CardTitle>
          <p className="text-sm text-slate-500 mt-1 dark:text-slate-400">
            Generate invoice + payment + jurnal akuntansi sekaligus untuk satu periode bulan tertentu. Customer tanpa invoice di periode tsb akan otomatis di-mark <b>lunas</b>.
          </p>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            <div>
              <label className="text-xs font-medium text-slate-600 dark:text-slate-300 block mb-1">Bulan Periode</label>
              <select value={bmpForm.period_month} onChange={(e) => { setBmpForm({...bmpForm, period_month: e.target.value}); setBmpPreview(null); }}
                className="w-full text-sm border rounded-md px-2 py-1.5 bg-white dark:bg-slate-800" data-testid="bmp-period-month">
                {Array.from({length:12},(_,i)=>i+1).map(m => <option key={m} value={m}>{['Januari','Februari','Maret','April','Mei','Juni','Juli','Agustus','September','Oktober','November','Desember'][m-1]}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs font-medium text-slate-600 dark:text-slate-300 block mb-1">Tahun</label>
              <select value={bmpForm.period_year} onChange={(e) => { setBmpForm({...bmpForm, period_year: e.target.value}); setBmpPreview(null); }}
                className="w-full text-sm border rounded-md px-2 py-1.5 bg-white dark:bg-slate-800" data-testid="bmp-period-year">
                {Array.from({length:6},(_,i)=>(new Date().getFullYear()-i+1)).map(y => <option key={y} value={y}>{y}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs font-medium text-slate-600 dark:text-slate-300 block mb-1">Metode Bayar</label>
              <select value={bmpForm.payment_method} onChange={(e) => setBmpForm({...bmpForm, payment_method: e.target.value})}
                className="w-full text-sm border rounded-md px-2 py-1.5 bg-white dark:bg-slate-800" data-testid="bmp-method">
                <option value="cash">Tunai (Cash)</option>
                <option value="transfer">Transfer Bank</option>
                <option value="qris">QRIS</option>
                <option value="ewallet">E-Wallet</option>
                <option value="other">Lainnya</option>
              </select>
            </div>
            <div>
              <label className="text-xs font-medium text-slate-600 dark:text-slate-300 block mb-1">Akun Debit (Kas/Bank)</label>
              <select value={bmpForm.cash_account_code} onChange={(e) => setBmpForm({...bmpForm, cash_account_code: e.target.value})}
                className="w-full text-sm border rounded-md px-2 py-1.5 bg-white dark:bg-slate-800" data-testid="bmp-cash-account">
                <option value="">— Pilih akun —</option>
                {(bmpAccounts || []).filter(a => (a.account_type || '').toLowerCase() === 'asset').map(a => (
                  <option key={a.id || a.code} value={a.code}>{a.code} — {a.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-xs font-medium text-slate-600 dark:text-slate-300 block mb-1">Akun Kredit (Pendapatan)</label>
              <select value={bmpForm.revenue_account_code} onChange={(e) => setBmpForm({...bmpForm, revenue_account_code: e.target.value})}
                className="w-full text-sm border rounded-md px-2 py-1.5 bg-white dark:bg-slate-800" data-testid="bmp-revenue-account">
                <option value="">— Pilih akun —</option>
                {(bmpAccounts || []).filter(a => ['revenue','pendapatan','income'].includes((a.account_type || '').toLowerCase())).map(a => (
                  <option key={a.id || a.code} value={a.code}>{a.code} — {a.name}</option>
                ))}
              </select>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="text-xs font-medium text-slate-600 dark:text-slate-300 block mb-1">Tgl. Bayar (opsional)</label>
                <input type="date" value={bmpForm.paid_date} onChange={(e) => setBmpForm({...bmpForm, paid_date: e.target.value})}
                  className="w-full text-sm border rounded-md px-2 py-1.5 bg-white dark:bg-slate-800" data-testid="bmp-paid-date" />
              </div>
              <div>
                <label className="text-xs font-medium text-slate-600 dark:text-slate-300 block mb-1">Next Due (opsional)</label>
                <input type="date" value={bmpForm.next_due_date} onChange={(e) => setBmpForm({...bmpForm, next_due_date: e.target.value})}
                  className="w-full text-sm border rounded-md px-2 py-1.5 bg-white dark:bg-slate-800" data-testid="bmp-next-due" />
              </div>
            </div>
          </div>

          <div className="flex flex-wrap gap-2 pt-2">
            <button type="button" onClick={() => callBulkPaid(true)} disabled={bmpLoading || bmpCommitting}
              className="px-4 py-2 rounded-md bg-white dark:bg-slate-800 border border-emerald-300 text-emerald-700 text-sm font-medium hover:bg-emerald-50 inline-flex items-center gap-1.5 disabled:opacity-50 dark:text-emerald-300 dark:hover:bg-emerald-900/30"
              data-testid="bmp-preview-btn">
              {bmpLoading ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Eye className="w-4 h-4" />}
              Preview (Dry-run)
            </button>
            <button type="button" onClick={commitBulkPaid} disabled={bmpCommitting || !bmpPreview || bmpPreview.dry_run !== true || !bmpPreview.eligible_count}
              className="px-4 py-2 rounded-md bg-emerald-600 text-white text-sm font-semibold hover:bg-emerald-700 inline-flex items-center gap-1.5 disabled:opacity-50"
              data-testid="bmp-commit-btn">
              {bmpCommitting ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              Eksekusi Pelunasan {bmpPreview?.eligible_count ? `(${bmpPreview.eligible_count})` : ''}
            </button>
            <button type="button" onClick={loadBmpAccounts}
              className="px-3 py-2 rounded-md bg-white dark:bg-slate-800 border border-slate-300 text-slate-700 text-sm hover:bg-slate-50 inline-flex items-center gap-1.5 dark:border-slate-600 dark:text-slate-200 dark:hover:bg-slate-700/50"
              data-testid="bmp-reload-coa">
              <RefreshCw className="w-4 h-4" /> Refresh COA
            </button>
          </div>

          {bmpPreview && (
            <div className={`border rounded-lg p-4 ${bmpPreview.dry_run ? 'bg-emerald-50/40 dark:bg-emerald-900/10 border-emerald-200' : 'bg-blue-50/40 dark:bg-blue-900/10 border-blue-200'}`}
              data-testid="bmp-result">
              <div className="text-sm font-semibold mb-2">
                {bmpPreview.dry_run ? 'Preview Hasil Dry-run' : 'Hasil Eksekusi'}
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs">
                <div><div className="text-slate-500 dark:text-slate-400">Periode</div><div className="font-mono">{bmpPreview.period}</div></div>
                <div><div className="text-slate-500 dark:text-slate-400">Customer</div><div className="font-mono">{bmpPreview.eligible_count ?? bmpPreview.processed_count}</div></div>
                <div><div className="text-slate-500 dark:text-slate-400">Total</div><div className="font-mono">Rp {(bmpPreview.total_amount || 0).toLocaleString('id-ID')}</div></div>
                <div><div className="text-slate-500 dark:text-slate-400">Paid Date</div><div className="font-mono">{bmpPreview.paid_date}</div></div>
                <div><div className="text-slate-500 dark:text-slate-400">Next Due</div><div className="font-mono">{bmpPreview.next_due_date}</div></div>
                <div><div className="text-slate-500 dark:text-slate-400">Dr</div><div className="font-mono">{bmpPreview.cash_account?.code} {bmpPreview.cash_account?.name}</div></div>
                <div><div className="text-slate-500 dark:text-slate-400">Cr</div><div className="font-mono">{bmpPreview.revenue_account?.code} {bmpPreview.revenue_account?.name}</div></div>
                <div><div className="text-slate-500 dark:text-slate-400">Metode</div><div className="font-mono">{bmpPreview.payment_method || bmpForm.payment_method}</div></div>
              </div>
              {bmpPreview.sample_customers && bmpPreview.sample_customers.length > 0 && (
                <div className="mt-3">
                  <div className="text-xs text-slate-500 mb-1 dark:text-slate-400">Contoh customer (5 pertama):</div>
                  <div className="text-xs font-mono bg-white dark:bg-slate-900 rounded border p-2 max-h-32 overflow-y-auto">
                    {bmpPreview.sample_customers.map(c => (
                      <div key={c.id}>#{c.id} {c.name} — Rp {(c.amount || 0).toLocaleString('id-ID')}</div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* ===== Login Audit & Brute-force Lockouts ===== */}
      <Card className="border-0 shadow-lg" data-testid="login-audit-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-amber-700 dark:text-amber-300">
            <Shield className="w-5 h-5" />
            Login Audit & Lockout Brute-Force
          </CardTitle>
          <p className="text-sm text-slate-500 mt-1 dark:text-slate-400">
            Sistem otomatis mengunci akun setelah <b>5x gagal login dalam 1 menit</b>, lockout berlaku 10 menit. Daftar akun terkunci & riwayat login bisa dilihat di bawah.
          </p>
        </CardHeader>
        <CardContent className="space-y-5">
          {/* Currently locked accounts */}
          <div className="border rounded-lg p-4 bg-amber-50/40 dark:bg-amber-900/10">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-semibold text-slate-800 dark:text-slate-200 flex items-center gap-2">
                <Lock className="w-4 h-4 text-amber-600 dark:text-amber-400" />
                Akun Terkunci Saat Ini
              </h3>
              <button type="button" onClick={loadLockouts} disabled={loadingLockouts}
                className="px-3 py-1.5 rounded-md bg-white dark:bg-slate-800 border border-amber-300 text-amber-700 text-xs font-medium hover:bg-amber-50 inline-flex items-center gap-1.5 disabled:opacity-50 dark:text-amber-300 dark:hover:bg-amber-900/30"
                data-testid="btn-reload-lockouts">
                <RefreshCw className={`w-3.5 h-3.5 ${loadingLockouts ? 'animate-spin' : ''}`} /> Refresh
              </button>
            </div>
            {lockouts && (lockouts.count === 0 ? (
              <div className="text-sm text-slate-500 text-center py-3 dark:text-slate-400"><CheckCircle className="w-5 h-5 text-green-500 mx-auto mb-1" />Tidak ada akun terkunci.</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead className="bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300">
                    <tr>
                      <th className="px-3 py-2 text-left">Username</th>
                      <th className="px-3 py-2 text-right">Gagal</th>
                      <th className="px-3 py-2 text-left">Last Fail</th>
                      <th className="px-3 py-2 text-right">Buka Lagi Dalam</th>
                      <th className="px-3 py-2 text-center">Aksi</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {lockouts.data.map(L => (
                      <tr key={L.username} className="bg-white dark:bg-slate-900">
                        <td className="px-3 py-2 font-mono">{L.username}</td>
                        <td className="px-3 py-2 text-right">{L.fail_count}</td>
                        <td className="px-3 py-2 text-xs text-slate-500 dark:text-slate-400">{L.last_fail_at ? new Date(L.last_fail_at).toLocaleString('id-ID') : '-'}</td>
                        <td className="px-3 py-2 text-right font-mono">{Math.ceil((L.retry_after_seconds || 0) / 60)} menit</td>
                        <td className="px-3 py-2 text-center">
                          <button type="button" onClick={() => unlockUser(L.username)} disabled={unlockingUsername === L.username}
                            className="px-2.5 py-1 rounded bg-emerald-600 text-white text-xs hover:bg-emerald-700 inline-flex items-center gap-1 disabled:opacity-50"
                            data-testid={`btn-unlock-${L.username}`}>
                            {unlockingUsername === L.username ? <RefreshCw className="w-3 h-3 animate-spin" /> : <Unlock className="w-3 h-3" />}
                            Unlock
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ))}
          </div>

          {/* Login history */}
          <div className="border rounded-lg p-4">
            <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
              <h3 className="font-semibold text-slate-800 dark:text-slate-200 flex items-center gap-2">
                <History className="w-4 h-4 text-slate-600 dark:text-slate-300" />
                Riwayat Login Terakhir
              </h3>
              <div className="flex items-center gap-2 flex-wrap">
                <input type="text" placeholder="Filter username/email" value={auditFilterUsername}
                  onChange={(e) => setAuditFilterUsername(e.target.value)}
                  className="text-xs border rounded-md px-2 py-1.5 bg-white dark:bg-slate-800 h-8 w-44"
                  data-testid="input-audit-filter" />
                <label className="text-xs flex items-center gap-1">
                  <input type="checkbox" checked={auditOnlyFailed} onChange={(e) => setAuditOnlyFailed(e.target.checked)} data-testid="chk-only-failed" />
                  Hanya gagal
                </label>
                <button type="button" onClick={loadAudit} disabled={loadingAudit}
                  className="px-3 py-1.5 rounded-md bg-indigo-600 text-white text-xs font-medium hover:bg-indigo-700 inline-flex items-center gap-1.5 disabled:opacity-50"
                  data-testid="btn-load-audit">
                  {loadingAudit ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <Eye className="w-3.5 h-3.5" />}
                  Lihat
                </button>
              </div>
            </div>
            {auditLogs && (
              <div className="overflow-x-auto max-h-96 [&::-webkit-scrollbar]:hidden [scrollbar-width:none]">
                <table className="w-full text-xs">
                  <thead className="bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 sticky top-0">
                    <tr>
                      <th className="px-3 py-2 text-left">Waktu</th>
                      <th className="px-3 py-2 text-left">Username</th>
                      <th className="px-3 py-2 text-center">Status</th>
                      <th className="px-3 py-2 text-left">Alasan / Role</th>
                      <th className="px-3 py-2 text-left">IP</th>
                      <th className="px-3 py-2 text-left">Browser</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {auditLogs.data.length === 0 ? (
                      <tr><td colSpan={6} className="px-3 py-4 text-center text-slate-500 dark:text-slate-400">Tidak ada data</td></tr>
                    ) : auditLogs.data.map(a => {
                      const ch = a.changes || {};
                      const ua = (ch.user_agent || '').slice(0, 60);
                      const reason = a.action === 'login_failed' ? (ch.reason || '-') : (ch.role || '-');
                      return (
                        <tr key={a.id} className="bg-white dark:bg-slate-900">
                          <td className="px-3 py-2 text-slate-500 dark:text-slate-400">{new Date(a.created_at).toLocaleString('id-ID')}</td>
                          <td className="px-3 py-2 font-mono">{a.username}</td>
                          <td className="px-3 py-2 text-center">
                            {a.action === 'login_success' ? (
                              <span className="px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700 text-[10px] dark:bg-emerald-900/40 dark:text-emerald-300">SUCCESS</span>
                            ) : (
                              <span className="px-1.5 py-0.5 rounded bg-red-100 text-red-700 text-[10px] dark:bg-red-900/40 dark:text-red-300">FAILED</span>
                            )}
                          </td>
                          <td className="px-3 py-2">{reason}</td>
                          <td className="px-3 py-2 font-mono text-slate-500 dark:text-slate-400">{a.ip_address || '-'}</td>
                          <td className="px-3 py-2 text-slate-400 dark:text-slate-500">{ua}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Danger Zone */}
      <Card className="border-0 shadow-lg border-red-200 dark:border-red-900">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-red-600 dark:text-red-400">
            <AlertTriangle className="w-5 h-5" />
            Danger Zone - Reset Data
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="p-3 bg-red-50 dark:bg-red-900/20 rounded-lg mb-4">
            <p className="text-sm text-red-600 dark:text-red-400">
              ⚠️ Perhatian: Operasi ini tidak dapat dibatalkan!
            </p>
          </div>
          
          <Button 
            onClick={handleResetInvoices}
            disabled={loading}
            className="w-full justify-start bg-indigo-500 hover:bg-indigo-600"
          >
            <Trash2 className="w-4 h-4 mr-2" />
            Reset Tagihan (Hapus Semua)
          </Button>
          
          <Button 
            onClick={handleResetPayments}
            disabled={loading}
            className="w-full justify-start bg-indigo-500 hover:bg-indigo-600"
          >
            <Trash2 className="w-4 h-4 mr-2" />
            Reset Pembayaran (Hapus Semua)
          </Button>
          
          <Button 
            onClick={handleResetDatabase}
            disabled={loading}
            className="w-full justify-start bg-red-600 hover:bg-red-700"
          >
            <Database className="w-4 h-4 mr-2" />
            Reset Database (Hapus Semua Data)
          </Button>
          
          <p className="text-xs text-slate-500 dark:text-slate-400">
            * Reset Database akan menghapus semua data kecuali users
          </p>
        </CardContent>
      </Card>
    </div>
  );
};

export default SystemTools;
